import { useEffect, useMemo, useRef } from "react";

import { LABOR_AMEND_BEFOREUNLOAD_MESSAGE } from "@/encounters/components/EncounterLabor/constants/labor-encounter-amend";
import { readLaborAmendPendingWorkFromSession } from "@/encounters/components/EncounterLabor/utils/labor-amend-session-state";

import { useLaborAmendFlowsheetPersistQueue } from "./use-labor-amend-flowsheet-persist-queue";
import { useLaborAmendPendingPreviews } from "./use-labor-amend-pending-previews";

interface UseLaborAmendFlowsheetPersistStateParams {
  encounterId: string;
  onDiscardAmendWork?: () => void;
}

/**
 * Composes queue and preview hooks and owns cross-cutting pending-work guards (beforeunload,
 * lost-work-after-refresh detection).
 */
export function useLaborAmendFlowsheetPersistState({
  encounterId,
  onDiscardAmendWork,
}: UseLaborAmendFlowsheetPersistStateParams) {
  const clearLocalPreviewsRef = useRef<() => void>(() => {});

  const persistQueue = useLaborAmendFlowsheetPersistQueue({
    encounterId,
    clearLocalPreviews: () => clearLocalPreviewsRef.current(),
    onDiscardAmendWork,
  });

  const pendingPreviews = useLaborAmendPendingPreviews({ encounterId });

  clearLocalPreviewsRef.current = pendingPreviews.clearLocalPreviews;

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!persistQueue.hasQueuedPersistJobs() && !pendingPreviews.hasLocalAmendPreviews) {
        return;
      }
      event.preventDefault();
      event.returnValue = LABOR_AMEND_BEFOREUNLOAD_MESSAGE;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [persistQueue, pendingPreviews.hasLocalAmendPreviews]);

  useEffect(() => {
    if (!readLaborAmendPendingWorkFromSession(encounterId)) {
      return;
    }
    if (persistQueue.hasQueuedPersistJobs() || pendingPreviews.hasLocalAmendPreviews) {
      persistQueue.setHasLostPendingWorkAfterRefresh(false);
      return;
    }
    persistQueue.setHasLostPendingWorkAfterRefresh(true);
  }, [encounterId, persistQueue, pendingPreviews.hasLocalAmendPreviews]);

  return useMemo(
    () => ({
      persistQueue,
      pendingPreviews,
    }),
    [persistQueue, pendingPreviews],
  );
}
